import {
  AorCancellationStatus,
  BenefitStatus,
  CarrierRequestStatus,
  EmployeeDto,
  EmployeesReportRowDto,
  EnrollmentStatus,
  EnrollmentTag,
  OnboardingPeriodDto,
  PaymentBy,
  Role,
  SelfEnrollType,
} from '@zorro/clients';
import { getFullName } from '@zorro/common-utils';

import { EnrollmentRow } from '../EnrollmentsDatatable';
import { EmployeeActionsOverlays } from './employeeActions.consts';

export type RecordForActions = {
  employeeId: string;
  userId: string;
  employerId: string;
  fullName: string;
  email: string;
  class?: string | null;
  customClassCategory?: string | null;
  role?: string;
  eligibleFrom?: string | null;
  eligibleUntil?: string | null;
  terminationDate?: string | null;
  leaveOfAbsenceStartDate?: string | null;
  leaveOfAbsenceEndDate?: string | null;
  leaveOfAbsencePaymentBy?: PaymentBy | null;
  onboardingPeriodId?: string;
  isOnboardingActive?: boolean;
  isOnboardingWaived?: boolean;
  enrollmentPeriodId?: string;
  enrollmentTeamId?: string | null;
  enrollmentTeamName?: string | null;
  benefitId?: string | null;
  enrollmentStatus?: EnrollmentStatus;
  benefitStatus?: BenefitStatus | null;
  state?: string | null;
  enrollmentStartDate?: string | null;
  enrollmentEndDate?: string | null;
  carrier?: string | null;
  isCombinedPlan?: boolean | null;
  selfEnroll?: SelfEnrollType | null;
  aorCancellationStatus?: AorCancellationStatus;
  carrierRequestStatus?: CarrierRequestStatus | null;
  enrollmentTags?: EnrollmentTag[];
  subEntity?: string | null;
  subEntity2?: string | null;
};

export type ActionsMenuSection = {
  recordsForActions: RecordForActions[];
  setOverlayId: (id: EmployeeActionsOverlays) => void;
};

export function mapEmployeeForActions(
  employee: EmployeeDto,
  onboardingPeriodId?: string,
  isOnboardingWaived?: boolean,
  role?: Role
): RecordForActions {
  return {
    onboardingPeriodId,
    employeeId: employee.id,
    userId: employee.userId,
    employerId: employee.employerId,
    fullName: getFullName(employee),
    email: employee.email,
    class: employee.class,
    customClassCategory: employee.customClassCategory,
    role,
    isOnboardingWaived,
    eligibleFrom: employee.eligibleFrom,
    eligibleUntil: employee.eligibleUntil,
    terminationDate: employee.terminationDate,
    leaveOfAbsenceStartDate: employee.leaveOfAbsence?.startDate,
    leaveOfAbsenceEndDate: employee.leaveOfAbsence?.endDate,
    leaveOfAbsencePaymentBy: employee.leaveOfAbsence?.paymentBy,
  };
}

export function mapEmployeeAndOnboardingPeriodForActions(
  employee: EmployeeDto,
  onboardingPeriod: OnboardingPeriodDto
): RecordForActions {
  return {
    employeeId: employee.id,
    userId: employee.userId,
    employerId: employee.employerId,
    fullName: getFullName(employee),
    email: employee.email,
    class: employee.class,
    customClassCategory: employee.customClassCategory,
    eligibleFrom: employee.eligibleFrom,
    eligibleUntil: employee.eligibleUntil,
    terminationDate: employee.terminationDate,
    leaveOfAbsenceStartDate: employee.leaveOfAbsence?.startDate,
    leaveOfAbsenceEndDate: employee.leaveOfAbsence?.endDate,
    leaveOfAbsencePaymentBy: employee.leaveOfAbsence?.paymentBy,
    onboardingPeriodId: onboardingPeriod.id,
    isOnboardingActive: onboardingPeriod.isActive,
    isOnboardingWaived: onboardingPeriod.isWaived,
    enrollmentPeriodId: onboardingPeriod.enrollmentPeriodId,
  };
}

export function mapEmployeeRowsForActions(
  employees: EmployeesReportRowDto[],
  enrollmentPeriodId?: string | null
): RecordForActions[] {
  return employees.map((employee): RecordForActions => {
    return {
      employeeId: employee.employeeId,
      userId: employee.userId,
      employerId: employee.employerId,
      fullName: employee.employeeFullName,
      email: employee.email,
      class: employee.class,
      customClassCategory: employee.customClassCategory,
      role: employee.employeeRole,
      eligibleFrom: employee.employeeEligibleFrom,
      eligibleUntil: employee.employeeEligibleUntil,
      terminationDate: employee.employeeTerminationDate,
      leaveOfAbsenceStartDate: employee.leaveOfAbsenceStartDate,
      leaveOfAbsenceEndDate: employee.leaveOfAbsenceEndDate,
      onboardingPeriodId: employee.onboardingPeriodId ?? undefined,
      isOnboardingActive: employee.onboardingPeriodIsActive ?? undefined,
      isOnboardingWaived: employee.onboardingPeriodIsWaived ?? undefined,
      enrollmentPeriodId: enrollmentPeriodId || '',
      benefitId: employee.benefitId,
      enrollmentStatus: employee.enrollmentStatus,
      benefitStatus: employee.benefitStatus,
      enrollmentStartDate: employee.enrollmentEffectiveFrom,
      enrollmentEndDate: employee.enrollmentEffectiveUntil,
      subEntity: employee.subEntityName,
      subEntity2: employee.subEntityName2,
    };
  });
}

export function mapEnrollmentRowsForActions(
  enrollments: EnrollmentRow[]
): RecordForActions[] {
  return enrollments.map((enrollment): RecordForActions => {
    return {
      employeeId: enrollment.employeeId,
      userId: enrollment.userId,
      employerId: enrollment.employerId,
      fullName: enrollment.fullName,
      email: enrollment.email,
      class: enrollment.employeeClass,
      customClassCategory: enrollment.customClassCategory,
      eligibleFrom: enrollment.eligibleFrom,
      eligibleUntil: enrollment.eligibleUntil,
      terminationDate: enrollment.employeeTerminationDate,
      leaveOfAbsenceStartDate: enrollment.leaveOfAbsenceStartDate,
      leaveOfAbsenceEndDate: enrollment.leaveOfAbsenceEndDate,
      onboardingPeriodId: enrollment.onboardingPeriodId,
      isOnboardingActive: enrollment.isOnboardingActive,
      isOnboardingWaived: enrollment.isOnboardingWaived,
      enrollmentTeamId: enrollment.enrollmentTeamId,
      enrollmentTeamName: enrollment.enrollmentTeamName,
      enrollmentPeriodId: enrollment.enrollmentPeriodId,
      benefitId: enrollment.benefitId,
      enrollmentStatus: enrollment.enrollmentStatus ?? undefined,
      benefitStatus: enrollment.benefitStatus,
      enrollmentStartDate: enrollment.enrollmentStartDate,
      enrollmentEndDate: enrollment.enrollmentEndDate,
      state: enrollment.state,
      carrier: enrollment.carrier,
      isCombinedPlan: enrollment.isCombinedPlan,
      selfEnroll: enrollment.selfEnroll,
      aorCancellationStatus: enrollment.aorCancellationStatus,
      carrierRequestStatus: enrollment.carrierRequestStatus,
      enrollmentTags: enrollment.enrollmentTags,
    };
  });
}
